import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Banknote, Eye, LogIn, Wallet } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { env } from '@/env';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api-client';
import { errorMessage as errMsg, errorStatus } from '@/lib/errors';
import { toast } from '@/components/ui/toast';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatDate, formatMoney, normalizeTimestamp, statusBadge, type StatusTone } from '@/components/data-table/columns-helper';
import { bangkokDateTime } from '@/lib/datetime.js';
import { moneyOrMinor, remainingMinor } from './withdraw-cap-format.js';
import { useListQuery } from '@/hooks/useListQuery';
import { useAgentCurrencyMap } from '@/hooks/useAgentCurrency';
import { useAuth } from '@/hooks/useAuth';
import { isImpersonationTab } from '@/lib/impersonation-bootstrap';
import { PageGuide } from '@/components/guide/PageGuide';
import { ActionDialog } from '@/pages/finance/ActionDialog';
import { AdjustDialog } from '@/pages/wallet/AdjustDialog';
import { walletMemberPath } from '@/pages/wallet/wallet-link.js';
import { isCreditAttempted, isRejectHazard } from '@/pages/promotion/claim-credit-attempt';
import { MemberBankFormDialog } from './MemberBankFormDialog';
import { MemberForm } from './MemberForm';
import { memberStatusTone } from './status-tone';
import { PASSWORD_MIN_LENGTH } from '@b1fr0st/contracts/common';

interface BankAccount {
  id: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  isDefault?: boolean;
  verified: boolean;
}

interface Group {
  id: string;
  code: string;
  name: string;
}

/**
 * The member row as svc-member serialises it.
 *
 * `phone`, `email`, `lineId` and every `accountNumber` arrive MASKED for a
 * caller without `member:reveal_pii` — the console renders what it is given and
 * never masks anything itself, because a value the browser received has already
 * left the server (ADR-0003).
 *
 * `birthDate` and `registeredIp` are a different state: HIDDEN, absent from the
 * payload entirely for the same caller. Hence optional, and hence the rows that
 * show them are omitted rather than rendered as a dash — a dash would say "this
 * player has no birth date", which is a different claim from "you may not see
 * it". An operator who HOLDS `member:reveal_pii` sees the row with a placeholder
 * and an eye instead: the level projection still withholds the value below
 * MANAGER, and the per-field reveal is the door that hands it over and records
 * that it did.
 */
interface MemberInfo {
  id: string;
  username: string;
  status: string;
  phone?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  lineId?: string | null;
  birthDate?: string | null;
  registeredIp?: string | null;
  referralCode?: string | null;
  /** null = follows the site's display currency. */
  currency?: string | null;
  createdAt?: string | null;
}

/** Which field the reveal endpoint is being asked for, and for which account. */
interface RevealRequest {
  /** Local key the revealed value is stored under. */
  key: string;
  field: 'phone' | 'email' | 'lineId' | 'bankAccount' | 'birthDate' | 'registeredIp';
  bankAccountId?: string;
}

// GET /members/:id returns the member row alongside its embedded groups and
// bank accounts — no separate sub-requests needed.
interface MemberDetail {
  member: MemberInfo;
  groups: Group[];
  bankAccounts: BankAccount[];
}

interface WalletView {
  creditCash: number;
  creditPromotion: number;
  balance: number;
  withdrawable: number;
  /** Immutable per wallet row (§2.7) — what every amount here is denominated in. */
  currency: string;
}

interface ImpersonateSubject {
  id: string;
  kind: 'player';
  agentId: string;
  currency: string;
  displayName: string;
}

interface ImpersonateMint {
  accessToken: string;
  expiresIn: number;
  subject: ImpersonateSubject;
}

interface Props {
  memberId: string | null;
  onClose: () => void;
}

export function MemberDetailDrawer({ memberId, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { hasPermission, canWrite } = useAuth();
  const open = !!memberId;
  const canImpersonate = hasPermission('member:impersonate');
  const canWriteMember = canWrite('member');
  // Its own permission, not `canWrite('member')`: `member:update` is what every
  // CS account holds, and overwriting a player's password is an account
  // takeover. `member:reset_password` is carved out of the `member:*` wildcard
  // for the same reason, so a holder of the wildcard alone does not see it either.
  const canResetPassword = hasPermission('member:reset_password');
  const canReadWallet = hasPermission('wallet:read');
  const canAdjustWallet = hasPermission('wallet:adjust');
  // An impersonation token carries the impersonated admin's permissions, and the
  // server refuses a reveal under it (MEMBER-431): offering the button would only fail.
  const canRevealPii = hasPermission('member:reveal_pii') && !isImpersonationTab();
  const [adjustOpen, setAdjustOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);

  // Retain the last opened member id so the drawer keeps rendering its data
  // through the Radix close animation. Driving the query off `memberId`
  // directly would blank the content the instant the prop goes null.
  const lastIdRef = React.useRef<string | null>(null);
  if (memberId) lastIdRef.current = memberId;
  const shownId = lastIdRef.current;

  const detailQ = useQuery<MemberDetail>({
    queryKey: ['/api/v1/members', shownId],
    queryFn: () => api.get(`/api/v1/members/${shownId}`),
    enabled: open,
  });

  /**
   * Fields revealed during THIS opening of the drawer, keyed by field (bank
   * accounts by `bank:<id>`, so revealing one account leaves the others masked).
   *
   * Local and deliberately not cached in React Query: a revealed value must last
   * exactly as long as the call it was needed for and must not be
   * waiting in a cache for the next player the operator opens.
   */
  const [revealed, setRevealed] = React.useState<Record<string, string | null>>({});
  /**
   * Which opening of the drawer a reveal answer belongs to.
   *
   * Bumped on every reset below. A reveal is a network round trip, and the
   * operator can move on while it is in flight: an answer for player A landing
   * after the drawer switched to player B would otherwise be written into B's
   * panel under B's labels, and one landing after a close-and-reopen would
   * un-mask a field in an opening that never asked for it. Each request carries
   * the opening it was made in, and an answer from any other opening is dropped.
   */
  const openingRef = React.useRef(0);
  // Keyed on the member as well as on open/closed. The drawer is a single
  // app-wide instance whose `memberId` prop changes when an operator opens the
  // next player from a list — no unmount, no close — so resetting on close
  // alone carried player A's revealed phone into player B's panel under B's
  // field labels.
  React.useEffect(() => {
    openingRef.current += 1;
    setRevealed({});
  }, [open, shownId]);

  const revealMutation = useMutation({
    mutationFn: async ({ key, field, bankAccountId }: RevealRequest) => {
      const opening = openingRef.current;
      const res = await api.get<{ value: string | null }>(`/api/v1/member-pii/${shownId}`, {
        params: { field, ...(bankAccountId ? { bankAccountId } : {}) },
      });
      return { key, value: res.value, opening };
    },
    onSuccess: ({ key, value, opening }) => {
      if (opening === openingRef.current) setRevealed((prev) => ({ ...prev, [key]: value }));
      // The operator is told the look-up is on the record, because it is: one
      // audit row per field per reveal, readable on Audit → Sensitive views.
      toast.success(t('members.revealRecorded'));
    },
    onError: (err) => toast.error(errMsg(err)),
  });

  /** The eye beside one maskable value. Absent entirely without the permission. */
  const revealButton = (request: RevealRequest) =>
    canRevealPii && revealed[request.key] === undefined ? (
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-1"
        aria-label={t('members.revealField')}
        title={t('members.revealField')}
        disabled={revealMutation.isPending}
        onClick={() => revealMutation.mutate(request)}
      >
        <Eye className="size-3.5" />
      </Button>
    ) : null;

  /**
   * One of the two HIDDEN fields, in whichever of its three states it is in.
   *
   * Absent from the payload AND unrevealable for this operator → no row at all,
   * because a dash would claim the player has no birth date. Absent but
   * revealable → a placeholder and an eye. Present (a MANAGER, or already
   * revealed here) → the value.
   */
  const hiddenField = (
    key: 'birthDate' | 'registeredIp',
    label: string,
    value: string | null | undefined,
  ) => {
    if (value === undefined && !canRevealPii) return null;
    if (value !== undefined) return <Field label={label}>{value ?? '-'}</Field>;
    const shown = key in revealed ? (revealed[key] ?? '-') : t('members.revealToSee');
    return (
      <Field label={label}>
        <MaskedValue value={shown} action={revealButton({ key, field: key })} />
      </Field>
    );
  };

  // Same query key AdjustDialog invalidates, so the balance refreshes after a credit.
  const walletQ = useQuery<WalletView>({
    queryKey: [`/api/v1/wallets/${shownId}`],
    queryFn: () => api.get(`/api/v1/wallets/${shownId}`),
    enabled: open && canReadWallet,
  });

  const statusMutation = useMutation({
    mutationFn: (nextStatus: string) =>
      api.post(`/api/v1/members/${shownId}/status`, { status: nextStatus }),
    onSuccess: () => {
      toast.success(t('members.statusUpdated'));
      qc.invalidateQueries({ queryKey: ['/api/v1/members'] });
    },
    onError: (err) => toast.error(errMsg(err)),
  });

  const [newPassword, setNewPassword] = React.useState('');
  const resetPwMutation = useMutation({
    // Its own resource, not a sub-path of `/members` — the gateway gates by path
    // prefix, so under `/members` this write is carried by `member:update`.
    mutationFn: (password: string) =>
      api.post('/api/v1/member-password-resets', { memberId: shownId, password }),
    onSuccess: () => {
      toast.success(t('members.passwordReset'));
      setNewPassword('');
    },
    onError: (err) => toast.error(errMsg(err)),
  });

  const resetMfaMutation = useMutation({
    mutationFn: () => api.post('/api/bo/member-mfa/reset', { memberId: shownId }),
    onSuccess: () => toast.success(t('members.mfaReset')),
    onError: (err) => toast.error(errMsg(err)),
  });

  const verifyBankMutation = useMutation({
    mutationFn: ({ bankId, verified }: { bankId: string; verified: boolean }) =>
      api.patch(`/api/v1/members/${shownId}/bank-accounts/${bankId}`, { verified }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['/api/v1/members', shownId] });
    },
    onError: (err) => toast.error(errMsg(err)),
  });

  const [bankDialogOpen, setBankDialogOpen] = React.useState(false);
  const [editingBank, setEditingBank] = React.useState<BankAccount | null>(null);
  const deleteBankMutation = useMutation({
    mutationFn: (bankId: string) =>
      api.delete(`/api/v1/members/${shownId}/bank-accounts/${bankId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/api/v1/members', shownId] }),
    onError: (err) => toast.error(errMsg(err)),
  });

  const [addGroupId, setAddGroupId] = React.useState('');
  const memberGroupsQ = useListQuery<{ id: string; name: string }>('/api/v1/member-groups', {
    pageSize: 200,
  });
  const addGroupMutation = useMutation({
    mutationFn: (groupId: string) => api.post(`/api/v1/members/${shownId}/groups`, { groupId }),
    onSuccess: () => {
      setAddGroupId('');
      qc.invalidateQueries({ queryKey: ['/api/v1/members', shownId] });
    },
    onError: (err) => toast.error(errMsg(err)),
  });
  const removeGroupMutation = useMutation({
    mutationFn: (groupId: string) => api.delete(`/api/v1/members/${shownId}/groups/${groupId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/api/v1/members', shownId] }),
    onError: (err) => toast.error(errMsg(err)),
  });

  const handleDeleteBank = async (bank: BankAccount) => {
    const ok = await confirm({
      title: t('members.confirmDeleteBank'),
      description: `${bank.bankCode} · ${bank.accountNumber}`,
      variant: 'destructive',
    });
    if (ok) deleteBankMutation.mutate(bank.id);
  };

  const handleRemoveGroup = async (group: Group) => {
    const ok = await confirm({
      title: t('members.confirmRemoveGroup'),
      description: group.name,
      variant: 'destructive',
    });
    if (ok) removeGroupMutation.mutate(group.id);
  };

  const [impersonateReason, setImpersonateReason] = React.useState('');
  const impersonateMutation = useMutation({
    mutationFn: (reason: string) =>
      api.post<ImpersonateMint>('/api/bo/member-impersonate', {
        memberId: shownId,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: (data) => {
      // Token travels via URL hash to a NEW tab on the PLAYER app. The player's
      // impersonation-bootstrap seeds a per-tab sessionStorage session and
      // strips the hash before render. Mirrors the agent flow in NodeDetailPanel.
      const payload = btoa(JSON.stringify({ t: data.accessToken, subject: data.subject }));
      window.open(
        `${env.VITE_PLAYER_APP_URL}/login/impersonate#data=` + encodeURIComponent(payload),
        '_blank',
        'noopener',
      );
      setImpersonateReason('');
    },
    onError: (err) => toast.error(errMsg(err)),
  });

  const member = detailQ.data?.member;
  const banks = detailQ.data?.bankAccounts ?? [];
  const groups = detailQ.data?.groups ?? [];

  const handleSuspend = async () => {
    const ok = await confirm({
      title: t('members.confirmSuspend'),
      description: member?.username,
      variant: 'destructive',
    });
    if (ok) statusMutation.mutate('suspended');
  };

  const handleActivate = () => statusMutation.mutate('active');

  const handleBlacklist = async () => {
    const ok = await confirm({
      title: t('members.confirmBlacklist'),
      description: member?.username,
      variant: 'destructive',
    });
    if (ok) statusMutation.mutate('blacklisted');
  };

  const handleResetPw = async () => {
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      toast.error(t('members.passwordMin', { min: PASSWORD_MIN_LENGTH }));
      return;
    }
    // Overwriting a player's password is an account takeover — the one
    // destructive action in this drawer that fired on a single click while
    // delete-bank, remove-group, suspend and blacklist all confirm (BO-06).
    const ok = await confirm({
      title: t('members.confirmResetPassword'),
      description: t('members.confirmResetPasswordDesc', { username: member?.username ?? '' }),
      variant: 'destructive',
    });
    if (!ok) return;
    resetPwMutation.mutate(newPassword);
  };

  // Flipping a bank account to verified marks a payout destination as trusted.
  const handleVerifyBank = async (bankId: string, next: boolean, accountNumber: string) => {
    const ok = await confirm({
      title: next ? t('members.confirmVerifyBank') : t('members.confirmUnverifyBank'),
      description: t('members.confirmVerifyBankDesc', { account: accountNumber }),
      variant: next ? 'default' : 'destructive',
    });
    if (!ok) return;
    verifyBankMutation.mutate({ bankId, verified: next });
  };

  const handleResetMfa = async () => {
    const ok = await confirm({
      title: t('members.confirmResetMfa'),
      description: t('members.confirmResetMfaDesc'),
      variant: 'destructive',
    });
    if (ok) resetMfaMutation.mutate();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>
            {member?.username ?? (detailQ.isLoading ? t('common.loading') : '-')}
          </SheetTitle>
          <SheetDescription>
            {member
              ? [
                  [member.firstName, member.lastName].filter(Boolean).join(' '),
                  member.phone,
                ]
                  .filter(Boolean)
                  .join(' · ') || member.id
              : ''}
          </SheetDescription>
        </SheetHeader>

        {canImpersonate && (
          <div className="mt-4 space-y-2 rounded-md border p-3">
            <Label htmlFor="impersonate-reason">{t('members.loginAsMember')}</Label>
            <p className="text-xs text-muted-foreground">{t('members.loginAsMemberHint')}</p>
            <div className="flex gap-2">
              <Input
                id="impersonate-reason"
                value={impersonateReason}
                onChange={(e) => setImpersonateReason(e.target.value)}
                placeholder={t('members.loginAsReason')}
              />
              <Button
                variant="outline"
                className="shrink-0"
                onClick={() => impersonateMutation.mutate(impersonateReason)}
                disabled={impersonateMutation.isPending}
              >
                <LogIn className="mr-1 size-3.5" />
                {t('members.loginAsMember')}
              </Button>
            </div>
          </div>
        )}

        <Tabs defaultValue="info" className="mt-4">
          {/* The wallet tab beside it shows the four balances; this leaves for
              the ledger behind them, which is the question a deposit or a
              withdrawal actually raises and which no drawer-sized panel
              answers. Same permission as that tab, so nobody is offered a link
              to a page that would refuse them. */}
          <div className="flex flex-wrap items-center gap-2">
            <TabsList>
              <TabsTrigger value="info">{t('members.info')}</TabsTrigger>
              <TabsTrigger value="banks">{t('members.banks')}</TabsTrigger>
              <TabsTrigger value="groups">{t('members.groups')}</TabsTrigger>
              <TabsTrigger value="promotion">{t('members.promotion')}</TabsTrigger>
              {canReadWallet && <TabsTrigger value="wallet">{t('members.wallet')}</TabsTrigger>}
              <TabsTrigger value="actions">{t('common.actions')}</TabsTrigger>
            </TabsList>
            {canReadWallet && shownId && (
              <Button
                variant="outline"
                size="sm"
                className="ms-auto shrink-0"
                // The app-wide instance of this drawer is mounted in
                // `MemberDrawerProvider`, OUTSIDE the router's `<Outlet/>`, so
                // it survives the route change: without closing it first the
                // operator arrives on /wallet under a modal sheet and its
                // overlay, with focus still trapped inside. `shownId` is the
                // retained ref, so reading it after `onClose` is safe.
                onClick={() => {
                  onClose();
                  navigate(walletMemberPath(shownId));
                }}
              >
                <Wallet className="mr-1 size-3.5" />
                {t('members.openWalletLedger')}
              </Button>
            )}
          </div>

          <TabsContent value="info" className="space-y-3">
            <TabGuide pageId="members.detailInfo" />
            {member && (
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Field label={t('common.status')}>
                  {statusBadge(member.status, memberStatusTone(member.status))}
                </Field>
                <Field label={t('members.phone')}>
                  <MaskedValue
                    value={revealed['phone'] ?? member.phone}
                    action={revealButton({ key: 'phone', field: 'phone' })}
                  />
                </Field>
                <Field label={t('members.email')}>
                  <MaskedValue
                    value={revealed['email'] ?? member.email}
                    action={revealButton({ key: 'email', field: 'email' })}
                  />
                </Field>
                <Field label={t('members.firstName')}>{member.firstName ?? '-'}</Field>
                <Field label={t('members.lastName')}>{member.lastName ?? '-'}</Field>
                <Field label={t('members.lineId')}>
                  <MaskedValue
                    value={revealed['lineId'] ?? member.lineId}
                    action={revealButton({ key: 'lineId', field: 'lineId' })}
                  />
                </Field>
                <Field label={t('members.referralCode')}>{member.referralCode ?? '-'}</Field>
                <Field label={t('members.playerCurrency')}>{member.currency ?? t('members.followSite')}</Field>
                <Field label={t('members.registeredAt')}>{formatDate(member.createdAt)}</Field>
                {/* Hidden, not masked: the server omits these two for a caller
                    without the reveal permission, and a dash here would read as
                    "this player has none". An operator who holds the permission
                    is offered the eye even when the level projection blanked the
                    row — otherwise the one rung that may reveal them (Finance,
                    level 3) has no control to press. */}
                {hiddenField('birthDate', t('members.birthDate'), member.birthDate)}
                {hiddenField('registeredIp', t('members.registeredIp'), member.registeredIp)}
              </dl>
            )}
            {canWriteMember && member && (
              <Button className="mt-4 w-full" onClick={() => setEditOpen(true)}>
                {t('common.edit')}
              </Button>
            )}
          </TabsContent>

          <TabsContent value="banks" className="space-y-3">
            <TabGuide pageId="members.detailBanks" />
            {canWriteMember && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingBank(null);
                    setBankDialogOpen(true);
                  }}
                >
                  {t('finance.addBankAccount')}
                </Button>
              </div>
            )}
            {detailQ.isLoading ? (
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : banks.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('common.noData')}</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {banks.map((b) => (
                  <li key={b.id} className="flex flex-col items-start justify-between p-3 gap-2">
                    <div className="flex items-start gap-2">
                      <div className="flex items-center gap-1 font-medium">
                        <span>
                          {b.bankCode} · {revealed[`bank:${b.id}`] ?? b.accountNumber}
                        </span>
                        {revealButton({
                          key: `bank:${b.id}`,
                          field: 'bankAccount',
                          bankAccountId: b.id,
                        })}
                      </div>
                      {b.isDefault && statusBadge(t('members.primaryBank'), 'info')}
                      {b.verified
                        ? statusBadge(t('members.bankVerified'), 'success')
                        : statusBadge(t('members.bankUnverified'), 'warning')}
                    </div>
                    {canWriteMember && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingBank(b);
                            setBankDialogOpen(true);
                          }}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={verifyBankMutation.isPending}
                          onClick={() => void handleVerifyBank(b.id, !b.verified, b.accountNumber)}
                        >
                          {b.verified ? t('members.unverifyBank') : t('members.verifyBank')}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={deleteBankMutation.isPending}
                          onClick={() => handleDeleteBank(b)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="groups" className="space-y-3">
            <TabGuide pageId="members.detailGroups" />
            <div className="flex items-center gap-2">
              <Select value={addGroupId} onValueChange={setAddGroupId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder={t('members.selectGroup')} />
                </SelectTrigger>
                <SelectContent>
                  {(memberGroupsQ.data?.items ?? [])
                    .filter((opt) => !groups.some((g) => g.id === opt.id))
                    .map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                disabled={!addGroupId || addGroupMutation.isPending}
                onClick={() => addGroupMutation.mutate(addGroupId)}
              >
                {t('members.addToGroup')}
              </Button>
            </div>
            {detailQ.isLoading ? (
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : groups.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('common.noData')}</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {groups.map((g) => (
                  <li key={g.id} className="flex items-center justify-between p-3">
                    <div>
                      <div className="font-medium">{g.name}</div>
                      <span className="text-xs text-muted-foreground">{g.code}</span>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={removeGroupMutation.isPending}
                      onClick={() => handleRemoveGroup(g)}
                    >
                      {t('common.delete')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="promotion" className="space-y-3">
            <TabGuide pageId="members.detailPromotion" />
            {shownId && <MemberPromotionsTab memberId={shownId} />}
          </TabsContent>

          {canReadWallet && (
            <TabsContent value="wallet" className="space-y-4">
              <TabGuide pageId="members.detailWallet" />
              {walletQ.isLoading ? (
                <div className="h-16 animate-pulse rounded bg-muted" />
              ) : walletQ.data ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {/* The wallet row carries its own denomination; these are its
                      own pool balances, so nothing else may decide it. */}
                  <Field label={t('wallet.creditCash')}>{formatMoney(walletQ.data.creditCash, { currency: walletQ.data.currency })}</Field>
                  <Field label={t('wallet.creditPromotion')}>{formatMoney(walletQ.data.creditPromotion, { currency: walletQ.data.currency })}</Field>
                  <Field label={t('wallet.balance')}>{formatMoney(walletQ.data.balance, { currency: walletQ.data.currency })}</Field>
                  <Field label={t('wallet.withdrawable')}>{formatMoney(walletQ.data.withdrawable, { currency: walletQ.data.currency })}</Field>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('common.noData')}</p>
              )}
              {canAdjustWallet && (
                <Button className="w-full" onClick={() => setAdjustOpen(true)}>{t('members.addMoney')}</Button>
              )}
            </TabsContent>
          )}

          {/* Suspend, blacklist and the password reset are writes; this tab had no
              permission check at all while the edit form above it did. The reset
              carries a second, narrower one of its own. */}
          <TabsContent value="actions" className="space-y-4">
            <TabGuide pageId="members.detailActions" />
            {!canWriteMember && !canResetPassword && (
              <p className="text-sm text-muted-foreground">{t('common.noPermission')}</p>
            )}
            {canWriteMember && (
              <>
                <div className="flex flex-wrap gap-2">
                  {member?.status === 'active' ? (
                    <Button variant="outline" onClick={handleSuspend} disabled={statusMutation.isPending}>
                      {t('members.suspend')}
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={handleActivate} disabled={statusMutation.isPending}>
                      {t('members.activate')}
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    onClick={handleBlacklist}
                    disabled={statusMutation.isPending || member?.status === 'blacklisted'}
                  >
                    {t('members.blacklist')}
                  </Button>
                </div>
              </>
            )}

            {canResetPassword && (
              <div className="space-y-2 rounded-md border p-3">
                <Label htmlFor="new-password">{t('users.resetPassword')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('auth.password')}
                  />
                  <Button onClick={() => void handleResetPw()} disabled={resetPwMutation.isPending}>
                    {t('common.save')}
                  </Button>
                </div>
              </div>
            )}

            {canWriteMember && (
              <div className="space-y-2 rounded-md border p-3">
                <Label>{t('members.resetMfa')}</Label>
                <p className="text-xs text-muted-foreground">{t('members.resetMfaHint')}</p>
                <Button
                  variant="outline"
                  onClick={handleResetMfa}
                  disabled={resetMfaMutation.isPending}
                >
                  {t('members.resetMfa')}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {shownId && (
          <AdjustDialog open={adjustOpen} onOpenChange={setAdjustOpen} memberId={shownId} currency={walletQ.data?.currency} />
        )}
        {member && (
          <MemberForm open={editOpen} onOpenChange={setEditOpen} editing={member} />
        )}
        {shownId && (
          <MemberBankFormDialog
            open={bankDialogOpen}
            onOpenChange={(v) => {
              setBankDialogOpen(v);
              if (!v) setEditingBank(null);
            }}
            memberId={shownId}
            editing={editingBank}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Each drawer tab documents itself.
 *
 * The tabs are six unrelated surfaces behind one header — a profile, a payout
 * destination list, promotion money and three irreversible account controls —
 * so one guide for "the drawer" would have to describe all six at once. A
 * right-aligned trigger per tab keeps each explanation next to the thing it
 * explains.
 */
function TabGuide({ pageId }: { pageId: string }) {
  return (
    <div className="flex justify-end">
      <PageGuide pageId={pageId} />
    </div>
  );
}

/**
 * One value that arrives masked, with its reveal control beside it.
 *
 * The console renders exactly what the server sent — it never masks, and it
 * never un-masks. `action` is null for an operator without the permission, so
 * nothing on screen offers a door they cannot open.
 */
function MaskedValue({ value, action }: { value?: string | null; action: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span>{value ?? '-'}</span>
      {value ? action : null}
    </span>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

interface Claim {
  id: string;
  promotionId: string | null;
  memberId: string;
  /**
   * The brand that granted the claim, and therefore the currency every amount
   * on this row is denominated in (§2.7). The member row cannot supply it
   * (`fieldsForLevel` withholds `agentId` below MANAGER) and neither can the
   * wallet (gated on `wallet:read`), so the claim carries it.
   */
  agentId: string | null;
  promotionTitle: string | null;
  promotionKind: string | null;
  grantedAmount: number;
  turnoverRequired: number;
  turnoverAchieved: number;
  status: string;
  rejectReason: string | null;
  expiresAt: string | null;
  createdAt: string;
  /** See `isCreditAttempted` — a stamped row may already have been paid. */
  creditAttemptedAt: string | null;
}

/** One claim holding a withdrawal ceiling over the member right now. */
interface WithdrawCapSource {
  claimId: string;
  promotionId: string;
  promotionName: string | null;
  /** The brand the ceiling is denominated in — see `moneyOrMinor`. */
  agentId: string | null;
  /** Raw minor units as stored — see `moneyOrMinor`. */
  capMinor: string;
  claimedAt: string;
}

interface WithdrawCapDetail {
  capMinor: string | null;
  agentId: string | null;
  since: string | null;
  sources: WithdrawCapSource[];
}

/** What svc-finance answers for the running total counted against the cap. */
interface WithdrawnSince {
  memberId: string;
  since: string | null;
  /** Exact minor units as a decimal string — see `remainingMinor`. */
  withdrawnSince: string;
}

/**
 * The promotion withdrawal ceiling in force for this member, read-only.
 *
 * The cap stopped being an editable promotion setting but is still
 * enforced on every withdraw, so a player refused with FIN-110 left support
 * with no screen showing the number or the campaign behind it. Nothing here
 * writes: the panel exists so the refusal can be explained, not overridden.
 * Hidden when no cap is in force, which is the ordinary case — but NOT when the
 * read failed: a 403 or a 500 rendered as "no cap" tells support the opposite
 * of what happened.
 *
 * FIN-110 compares the ceiling against what the member has ALREADY taken out
 * since it took effect, so a panel naming only the ceiling answers "how much
 * are they capped at" and not "how much is left" — the question an operator is
 * actually holding an approval open to settle. svc-promo owns the
 * ceiling and svc-finance owns the withdrawals counted against it, and the two
 * schemas cannot be joined (§4.1), so each is asked for its own half.
 */
function WithdrawCapPanel({ memberId }: { memberId: string }) {
  const { t } = useTranslation();
  // The claim carries the brand, and the brand carries the currency. The member
  // row cannot be used for it (`fieldsForLevel` withholds `agentId` below
  // MANAGER) and neither can the wallet (gated on `wallet:read`, which the
  // operator reading this panel need not hold). The resolver answers null while
  // the org list is in flight or refused, and null renders as minor units.
  const resolveCurrency = useAgentCurrencyMap();
  const capQ = useQuery<WithdrawCapDetail>({
    queryKey: ['/api/v1/promotion-claims/withdraw-cap', memberId],
    queryFn: () =>
      api.get(`/api/v1/promotion-claims/withdraw-cap?memberId=${encodeURIComponent(memberId)}`),
  });
  const detail = capQ.data;
  const capMinor = detail?.capMinor ?? null;
  const since = detail?.since ?? null;
  // Only asked for once a ceiling is actually in force — an uncapped member has
  // nothing to compare a running total against, and this read costs a
  // `withdraw:read` an operator may not hold.
  const withdrawnQ = useQuery<WithdrawnSince>({
    queryKey: ['/api/v1/withdraws/withdrawn-since', memberId, since],
    queryFn: () =>
      api.get(
        `/api/v1/withdraws/withdrawn-since?memberId=${encodeURIComponent(memberId)}` +
          // svc-promo answers `since` as raw Postgres text ("… 10:00:00+00"),
          // which is not the RFC 3339 instant svc-finance validates.
          (since ? `&since=${encodeURIComponent(normalizeTimestamp(since))}` : ''),
      ),
    enabled: !!capMinor,
  });

  if (capQ.isError) {
    return (
      <section className="rounded-md border border-destructive/50 bg-destructive/5 p-3">
        <h4 className="text-sm font-medium">{t('promotion.withdrawCap.title')}</h4>
        <p className="mt-1 text-xs text-destructive">
          {/* "Your role cannot read this" and "this is broken" call for
              opposite next steps, and both used to render as one sentence. */}
          {errorStatus(capQ.error) === 403
            ? t('promotion.withdrawCap.forbidden')
            : t('promotion.withdrawCap.unreadable')}
        </p>
      </section>
    );
  }
  if (!detail || !capMinor || detail.sources.length === 0) return null;

  const currency = resolveCurrency(detail.agentId);
  const withdrawnMinor = withdrawnQ.data?.withdrawnSince ?? null;
  const remaining = remainingMinor(capMinor, withdrawnMinor);
  const withdrawnText = withdrawnQ.isError
    ? errorStatus(withdrawnQ.error) === 403
      ? t('promotion.withdrawCap.forbidden')
      : t('promotion.withdrawCap.withdrawnUnreadable')
    : withdrawnMinor === null
      ? t('common.loading')
      : moneyOrMinor(withdrawnMinor, currency, t);

  return (
    <section className="space-y-2 rounded-md border border-amber-500/60 bg-amber-500/5 p-3">
      <h4 className="text-sm font-medium">{t('promotion.withdrawCap.title')}</h4>
      <p className="text-xs text-muted-foreground">{t('promotion.withdrawCap.hint')}</p>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{t('promotion.withdrawCap.effective')}</span>
        <span className="font-medium">{moneyOrMinor(capMinor, currency, t)}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{t('promotion.withdrawCap.withdrawn')}</span>
        <span
          className={`font-medium ${withdrawnQ.isError ? 'text-destructive' : ''}`}
        >
          {withdrawnText}
        </span>
      </div>
      {remaining !== null && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('promotion.withdrawCap.remaining')}</span>
          <span className="font-medium">{moneyOrMinor(remaining, currency, t)}</span>
        </div>
      )}
      {since && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{t('promotion.withdrawCap.since')}</span>
          {/* Bangkok, not the viewer's zone: the cap counts withdrawals from an
              INSTANT, and support comparing it against a withdrawal list has to
              be reading the same clock the platform's business day runs on
              (§3.9). The zone is on screen so the figure is not ambiguous. */}
          <span>
            {bangkokDateTime(since)} {t('promotion.withdrawCap.tzBangkok')}
          </span>
        </div>
      )}
      <ul className="divide-y rounded-md border bg-background">
        {detail.sources.map((src) => (
          <li key={src.claimId} className="flex items-start justify-between gap-2 p-2 text-xs">
            <div className="min-w-0">
              <div className="truncate font-medium">
                {src.promotionName ?? t('promotion.withdrawCap.unnamedPromotion')}
              </div>
              <div className="mt-0.5 truncate text-muted-foreground">
                {t('promotion.withdrawCap.claimId')}: {src.claimId}
              </div>
            </div>
            <span className="shrink-0 font-medium">
              {moneyOrMinor(src.capMinor, resolveCurrency(src.agentId), t)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const CLAIM_TONE: Record<string, StatusTone> = {
  pending: 'warning',
  approved: 'info',
  active: 'info',
  completed: 'success',
  released: 'warning',
  forfeited: 'muted',
  revoked: 'danger',
  rejected: 'danger',
  cancelled: 'muted',
};

// ponytail: show the latest 50 claims with no pager — a member with >50
// promotion claims is an edge; add pagination here if that ever shows up.
function MemberPromotionsTab({ memberId }: { memberId: string }) {
  const { t } = useTranslation();
  // Every amount on a claim row is denominated in the granting brand's
  // currency, not the console's. Read from the claim's own agent so the tab
  // needs no `wallet:read`, and rendered as labelled minor units when the
  // resolver does not know — never as a confident THB.
  const resolveCurrency = useAgentCurrencyMap();
  const { canWrite: can } = useAuth();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const canWrite = can('promotion');
  const [reject, setReject] = React.useState<{ id: string; creditAttempted: boolean } | null>(
    null,
  );

  const claimsQ = useListQuery<Claim>('/api/v1/promotion-claims', {
    memberId,
    page: 1,
    pageSize: 50,
  });
  const items = claimsQ.data?.items ?? [];
  const total = claimsQ.data?.total ?? 0;

  const actionM = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'release' | 'revoke' }) =>
      api.post(`/api/v1/promotion-claims/${id}/action`, { action }),
    onSuccess: () => {
      toast.success(t('common.save'));
      qc.invalidateQueries({ queryKey: ['/api/v1/promotion-claims'] });
    },
    onError: (err) => toast.error(errMsg(err)),
  });

  const handleRevoke = async (c: Claim) => {
    const ok = await confirm({
      title: t('members.confirmRevoke'),
      description: t('members.confirmRevokeDesc'),
      variant: 'destructive',
    });
    if (ok) actionM.mutate({ id: c.id, action: 'revoke' });
  };

  return (
    <div className="space-y-3">
      <WithdrawCapPanel memberId={memberId} />
      <p className="text-sm text-muted-foreground">
        {t('members.promotion')} ({total})
      </p>
      {claimsQ.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('common.noData')}</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {items.map((c) => {
            const canApprove = c.status === 'pending';
            const canRevoke = c.status === 'active';
            const pct =
              c.turnoverRequired > 0
                ? Math.min(100, Math.round((c.turnoverAchieved / c.turnoverRequired) * 100))
                : 100;
            const claimCurrency = resolveCurrency(c.agentId);
            return (
              <li key={c.id} className="space-y-2 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {c.promotionTitle ?? t('promotion.claims')}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2">
                      {c.promotionKind &&
                        statusBadge(
                          t(`promotion.kind.${c.promotionKind}`, { defaultValue: c.promotionKind }),
                          'muted',
                        )}
                      <span className="text-xs text-muted-foreground">
                        {formatDate(c.createdAt)}
                      </span>
                    </div>
                  </div>
                  {statusBadge(
                    t(`promotion.claimStatus.${c.status}`, { defaultValue: c.status }),
                    CLAIM_TONE[c.status] ?? 'default',
                  )}
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('promotion.bonus')}</span>
                  <span className="font-medium">
                    {moneyOrMinor(c.grantedAmount, claimCurrency, t)}
                  </span>
                </div>

                {c.turnoverRequired > 0 && (
                  <div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('promotion.turnover')}</span>
                      <span>
                        {moneyOrMinor(c.turnoverAchieved, claimCurrency, t)} /{' '}
                        {moneyOrMinor(c.turnoverRequired, claimCurrency, t)} ({pct}%)
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}

                {c.expiresAt && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t('members.expiresAt')}</span>
                    <span>{formatDate(c.expiresAt)}</span>
                  </div>
                )}

                {/* Shown whenever the stamp exists, but coloured as a hazard
                    only where Reject would destroy a lock on money already
                    paid. Every ordinary approval carries the stamp too, and an
                    alarm on all of them is an alarm on none. */}
                {isCreditAttempted(c) && (
                  <div
                    className={`flex items-center justify-between text-xs ${
                      isRejectHazard(c) ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {isRejectHazard(c) && (
                        <Banknote className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      )}
                      {t('promotion.creditAttempted')}
                    </span>
                    <span>{formatDate(c.creditAttemptedAt)}</span>
                  </div>
                )}

                {c.rejectReason && (
                  <div className="text-xs text-destructive">{c.rejectReason}</div>
                )}

                {(canApprove || canRevoke) && (
                  <div className="flex flex-wrap justify-end gap-2 pt-1">
                    {canApprove && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!canWrite || actionM.isPending}
                          onClick={() => actionM.mutate({ id: c.id, action: 'approve' })}
                        >
                          {t('promotion.approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive"
                          disabled={!canWrite}
                          onClick={() =>
                            setReject({ id: c.id, creditAttempted: isRejectHazard(c) })
                          }
                        >
                          {t('promotion.reject')}
                        </Button>
                      </>
                    )}
                    {canRevoke && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!canWrite || actionM.isPending}
                          onClick={() => actionM.mutate({ id: c.id, action: 'release' })}
                        >
                          {t('promotion.release')}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={!canWrite || actionM.isPending}
                          onClick={() => handleRevoke(c)}
                        >
                          {t('promotion.revoke')}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <ActionDialog
        open={reject !== null}
        onOpenChange={(o) => !o && setReject(null)}
        pathBase={reject ? `/api/v1/promotion-claims/${reject.id}` : null}
        action="reject"
        invalidateKey="/api/v1/promotion-claims"
        // Read off the row the operator clicked, not off the list: a refetch
        // between opening the dialog and confirming must not quietly drop the
        // hazard the confirmation exists for.
        acknowledgement={reject?.creditAttempted ? t('promotion.creditAttemptedWarning') : null}
      />
    </div>
  );
}
